import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement, nothing, type PropertyValues } from 'lit';
import { live } from 'lit/directives/live.js';
import '../../../components/confirmation-dialog';
import { emitToast } from '../../../components/toast-events';
import '@spectrum-web-components/textfield/sp-textfield.js';
import '@spectrum-web-components/switch/sp-switch.js';
import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';
import '../components/property-table';
import './inspector/code-editor';
import { property, state } from 'lit/decorators.js';
import { api } from '../../../api';
import type { RuntimeValue, WorkflowWebhook } from '../../../api/types';
import { workflowService } from '../../../services/workflow-service';
import { formatDateTime } from '../../../utils/format';
import type { PropertyItem } from '../components/property-table';
import { propertyGridStyles } from './property-grid.styles';
import {
  getConfigRuntimeValues,
  getSecretRuntimeValues,
  hideRuntimeValue,
  mergeRevealedRuntimeValue,
} from './runtime-values-utils';

const DEFAULT_CRON_TIMEZONE = 'Europe/Berlin';

const VIEWER_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
})();

interface FormattedScheduleInfo {
  scheduledLabel: string;
  localLabel: string;
  showLocal: boolean;
}

@localized()
export class SbSettings extends LitElement {
  static styles = [
    propertyGridStyles,
    css`
    :host {
      width: 100%;
    }
    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
      margin-top: 8px;
      margin-bottom: 8px;
    }
    .hint {
      font-size: 12px;
      color: var(--spectrum-gray-600);
      line-height: 1.4;
    }
    .hint.secondary {
      color: var(--spectrum-gray-700);
    }
    .error {
      font-size: 12px;
      color: var(--spectrum-global-color-red-600, #d7373f);
    }
    .hint-group {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .webhook-description {
      margin: 0 0 10px;
      color: var(--spectrum-gray-600);
      font-size: 12px;
      line-height: 1.4;
    }
    .webhook-actions {
      margin-bottom: 10px;
    }
    .webhook-list {
      display: flex;
      flex-direction: column;
      gap: var(--gap-2);
    }
    .webhook-item {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 10px;
      border: 1px solid var(--color-app-border);
      border-radius: 8px;
      background: var(--color-sidebar-surface);
    }
    .webhook-content {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .webhook-url {
      font-family: var(--font-mono, 'SFMono-Regular', Consolas, 'Liberation Mono', monospace);
      font-size: 11px;
      word-break: break-all;
      color: var(--spectrum-gray-900);
    }
    .webhook-meta {
      font-size: 11px;
      color: var(--spectrum-gray-600);
    }
    .webhook-buttons {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
    }
    .webhook-empty {
      padding: 10px 0;
      color: var(--spectrum-gray-600);
      font-size: 12px;
    }
  `,
  ];

  @property({ type: Number })
  accessor workflowId: number | undefined;

  @property({ attribute: false })
  accessor workflowsList: any = null;

  @state()
  accessor runtimeValues: RuntimeValue[] = [];

  @state()
  accessor revealedRuntimeValueIds = new Set<string>();

  @state()
  accessor runtimeValueErrors: Record<'config' | 'secret', string | null> = {
    config: null,
    secret: null,
  };

  private pendingRuntimeValues = new Set<string>();

  @state()
  accessor cronString = '';

  @state()
  accessor cronTimezone = DEFAULT_CRON_TIMEZONE;

  @state()
  accessor cronError = '';

  @state()
  accessor cronSaving = false;

  @state()
  accessor cronLoading = false;

  @state()
  accessor cronNextExecutionAt: string | null = null;

  @state()
  accessor cronLastExecutedAt: string | null = null;

  @state()
  accessor workflowName = '';

  @state()
  accessor workflowNameSaving = false;

  @state()
  accessor workflowNameError = '';

  @state()
  accessor workflowDescription = '';

  @state()
  accessor workflowDescriptionSaving = false;

  @state()
  accessor workflowDescriptionError = '';

  @state()
  accessor workflowIsTemplate = false;

  @state()
  accessor workflowTemplateSaving = false;

  @state()
  accessor workflowTemplateError = '';

  private cronInitialString = '';
  private cronInitialTimezone = DEFAULT_CRON_TIMEZONE;
  private workflowNameInitial = '';
  private workflowDescriptionInitial = '';
  private workflowTemplateInitial = false;
  private activeWorkflowRequest?: number;
  private readonly viewerTimezone = VIEWER_TIMEZONE;

  @state()
  accessor webhooks: WorkflowWebhook[] = [];

  @state()
  accessor webhookLoading = false;

  @state()
  accessor webhookError: string | null = null;

  @state()
  accessor pendingWebhookDelete: WorkflowWebhook | null = null;

  @state()
  accessor pendingRuntimeValueDelete: {
    kind: 'config' | 'secret';
    item: PropertyItem;
  } | null = null;

  private cronRefreshTimer: number | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    void this.loadRuntimeValues();
    window.addEventListener(
      'runtimeValueCreated',
      this.onRuntimeValueCreated as EventListener
    );
    window.addEventListener(
      'runtimeValueChanged',
      this.onRuntimeValueChangedWs as EventListener
    );
    window.addEventListener(
      'runtimeValueRemoved',
      this.onRuntimeValueRemovedWs as EventListener
    );
    window.addEventListener(
      'workflowUpdated',
      this.onWorkflowUpdated as EventListener
    );
  }

  protected override updated(changed: PropertyValues<this>): void {
    if (changed.has('workflowId')) {
      this.pendingWebhookDelete = null;
      if (typeof this.workflowId === 'number') {
        this.cronError = '';
        void this.fetchWorkflowCron(this.workflowId);
        void this.loadWebhooks(this.workflowId);
      } else {
        this.activeWorkflowRequest = undefined;
        this.cronLoading = false;
        this.resetCronState();
        this.webhooks = [];
        this.webhookError = null;
        this.webhookLoading = false;
      }
    }
  }

  disconnectedCallback(): void {
    window.removeEventListener(
      'runtimeValueCreated',
      this.onRuntimeValueCreated as EventListener
    );
    window.removeEventListener(
      'runtimeValueChanged',
      this.onRuntimeValueChangedWs as EventListener
    );
    window.removeEventListener(
      'runtimeValueRemoved',
      this.onRuntimeValueRemovedWs as EventListener
    );
    window.removeEventListener(
      'workflowUpdated',
      this.onWorkflowUpdated as EventListener
    );
    this.ensureCronRefreshTimer(false);
    super.disconnectedCallback();
  }

  private resetCronState() {
    this.cronString = '';
    this.cronTimezone = DEFAULT_CRON_TIMEZONE;
    this.cronInitialString = '';
    this.cronInitialTimezone = DEFAULT_CRON_TIMEZONE;
    this.cronError = '';
    this.cronNextExecutionAt = null;
    this.cronLastExecutedAt = null;
    this.workflowName = '';
    this.workflowNameInitial = '';
    this.workflowNameError = '';
    this.workflowNameSaving = false;
    this.workflowDescription = '';
    this.workflowDescriptionInitial = '';
    this.workflowDescriptionError = '';
    this.workflowDescriptionSaving = false;
    this.workflowIsTemplate = false;
    this.workflowTemplateInitial = false;
    this.workflowTemplateError = '';
    this.workflowTemplateSaving = false;
  }

  private async loadRuntimeValues() {
    try {
      this.runtimeValues = await api.runtimeValues.list();
      this.runtimeValueErrors = {
        config: null,
        secret: null,
      };
    } catch (error) {
      console.error('Failed to load runtime values', error);
      this.runtimeValues = [];
      this.runtimeValueErrors = {
        config: msg('Failed to load runtime values'),
        secret: msg('Failed to load runtime values'),
      };
    }
  }

  private async loadWebhooks(workflowId: number) {
    this.webhookLoading = true;
    this.webhookError = null;
    const currentId = workflowId;
    try {
      const webhooks = await workflowService.listWebhooks(workflowId);
      if (this.workflowId !== currentId) {
        return;
      }
      this.webhooks = webhooks;
    } catch (error) {
      console.error('Failed to load webhooks', error);
      this.webhookError = msg('Failed to load webhooks');
      this.webhooks = [];
    } finally {
      this.webhookLoading = false;
    }
  }

  private async createWebhook() {
    if (typeof this.workflowId !== 'number') {
      return;
    }
    const workflowId = this.workflowId;
    this.webhookLoading = true;
    try {
      const webhook = await workflowService.createWebhook(workflowId);
      if (this.workflowId !== workflowId) {
        return;
      }
      this.webhooks = [...this.webhooks, webhook];
      this.webhookError = null;
    } catch (error) {
      console.error('Failed to create webhook', error);
      this.webhookError = msg('Failed to create webhook');
    } finally {
      this.webhookLoading = false;
    }
  }

  private requestDeleteWebhook(webhook: WorkflowWebhook) {
    this.pendingWebhookDelete = webhook;
  }

  private cancelDeleteWebhook = () => {
    this.pendingWebhookDelete = null;
  };

  private confirmDeleteWebhook = async () => {
    const webhook = this.pendingWebhookDelete;
    if (!webhook) {
      return;
    }
    if (typeof this.workflowId !== 'number') {
      this.pendingWebhookDelete = null;
      return;
    }
    this.pendingWebhookDelete = null;
    const workflowId = this.workflowId;
    this.webhookLoading = true;
    try {
      await workflowService.deleteWebhook(workflowId, webhook.id);
      if (this.workflowId !== workflowId) {
        return;
      }
      this.webhooks = this.webhooks.filter((item) => item.id !== webhook.id);
      this.webhookError = null;
    } catch (error) {
      console.error('Failed to delete webhook', error);
      this.webhookError = msg('Failed to delete webhook');
    } finally {
      this.webhookLoading = false;
    }
  };

  private async copyWebhook(webhook: WorkflowWebhook) {
    const url = workflowService.getWebhookUrl(webhook);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        emitToast(this, {
          variant: 'error',
          message: msg(
            'Clipboard is unavailable. Select the webhook URL to copy it.'
          ),
        });
        return;
      }
      emitToast(this, {
        variant: 'success',
        message: msg('Webhook URL copied'),
      });
    } catch (error) {
      console.error('Failed to copy webhook URL', error);
      emitToast(this, {
        variant: 'error',
        message: msg('Failed to copy webhook URL'),
      });
    }
  }

  private async copyWebhookId(webhook: WorkflowWebhook) {
    const id = webhook.id;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(id);
      } else {
        emitToast(this, {
          variant: 'error',
          message: msg(
            'Clipboard is unavailable. Select the webhook ID to copy it.'
          ),
        });
        return;
      }
      emitToast(this, {
        variant: 'success',
        message: msg('Webhook ID copied'),
      });
    } catch (error) {
      console.error('Failed to copy webhook ID', error);
      emitToast(this, {
        variant: 'error',
        message: msg('Failed to copy webhook ID'),
      });
    }
  }

  private renderWebhookList() {
    if (this.webhookLoading) {
      return html`<div class="hint">${msg('Loading webhooks...')}</div>`;
    }
    if (this.webhookError) {
      return html`<div class="error">${this.webhookError}</div>`;
    }
    if (this.webhooks.length === 0) {
      return html`<div class="webhook-empty">${msg('No webhooks configured yet.')}</div>`;
    }
    return html`
      <div class="webhook-list">
        ${this.webhooks.map((webhook) => {
          const url = workflowService.getWebhookUrl(webhook);
          return html`
            <div class="webhook-item">
              <div class="webhook-content">
                <div class="webhook-url">${url}</div>
                <div class="webhook-meta">ID: ${webhook.id}</div>
              </div>
              <div class="webhook-buttons">
                <sp-action-button
                  quiet
                  label=${msg('Copy URL')}
                  @click=${() => this.copyWebhook(webhook)}
                >
                  ${msg('Copy URL')}
                </sp-action-button>
                <sp-action-button
                  quiet
                  label=${msg('Copy ID')}
                  @click=${() => this.copyWebhookId(webhook)}
                >
                  ${msg('Copy ID')}
                </sp-action-button>
                <sp-action-button
                  quiet
                  label=${msg('Delete webhook')}
                  ?disabled=${this.webhookLoading}
                  @click=${() => this.requestDeleteWebhook(webhook)}
                >
                  ${msg('Delete')}
                </sp-action-button>
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }

  private ensureCronRefreshTimer(active: boolean) {
    if (active) {
      if (this.cronRefreshTimer !== null) {
        return;
      }
      this.cronRefreshTimer = window.setInterval(() => {
        void this.refreshCronMetadata();
      }, 30_000);
      void this.refreshCronMetadata();
      return;
    }
    if (this.cronRefreshTimer !== null) {
      window.clearInterval(this.cronRefreshTimer);
      this.cronRefreshTimer = null;
    }
  }

  private async refreshCronMetadata() {
    if (typeof this.workflowId !== 'number' || !this.cronInitialString.trim()) {
      return;
    }
    const workflowId = this.workflowId;
    try {
      const workflow = await api.workflows.get(workflowId);
      if (this.workflowId !== workflowId) {
        return;
      }
      this.cronLastExecutedAt = workflow.cronLastExecutedAt ?? null;
      this.cronNextExecutionAt = workflow.cronNextExecutionAt ?? null;
    } catch (error) {
      console.error('Failed to refresh workflow schedule metadata', error);
    }
  }
  private async fetchWorkflowCron(id: number) {
    this.activeWorkflowRequest = id;
    this.cronLoading = true;
    try {
      const workflow = await api.workflows.get(id);
      if (this.activeWorkflowRequest !== id) {
        return;
      }
      const workflowName = this.normalizeWorkflowName(workflow);
      this.workflowName = workflowName;
      this.workflowNameInitial = workflowName;
      this.workflowNameError = '';
      this.workflowNameSaving = false;
      this.workflowDescription = workflow.description ?? '';
      this.workflowDescriptionInitial = this.workflowDescription;
      this.workflowDescriptionError = '';
      this.workflowDescriptionSaving = false;
      this.workflowIsTemplate = workflow.isTemplate ?? false;
      this.workflowTemplateInitial = this.workflowIsTemplate;
      this.workflowTemplateError = '';
      this.workflowTemplateSaving = false;
      const cron = workflow.cronString ?? '';
      const timezone = workflow.cronTimezone ?? DEFAULT_CRON_TIMEZONE;
      this.cronString = cron;
      this.cronTimezone = cron ? timezone : DEFAULT_CRON_TIMEZONE;
      this.cronInitialString = cron;
      this.cronInitialTimezone = cron ? timezone : DEFAULT_CRON_TIMEZONE;
      this.cronNextExecutionAt = workflow.cronNextExecutionAt ?? null;
      this.cronLastExecutedAt = workflow.cronLastExecutedAt ?? null;
      this.cronError = '';
    } catch (error) {
      if (this.activeWorkflowRequest === id) {
        this.cronError =
          error instanceof Error
            ? error.message
            : msg('Failed to load workflow settings');
        this.resetCronState();
        this.cronNextExecutionAt = null;
      }
    } finally {
      if (this.activeWorkflowRequest === id) {
        this.cronLoading = false;
        this.activeWorkflowRequest = undefined;
      }
    }
  }

  private get cronDirty(): boolean {
    const current = this.cronString.trim();
    const initial = this.cronInitialString.trim();
    if (current !== initial) {
      return true;
    }
    if (!current && !initial) {
      return false;
    }
    const currentTz =
      (this.cronTimezone || DEFAULT_CRON_TIMEZONE).trim() ||
      DEFAULT_CRON_TIMEZONE;
    const initialTz =
      (this.cronInitialTimezone || DEFAULT_CRON_TIMEZONE).trim() ||
      DEFAULT_CRON_TIMEZONE;
    return currentTz !== initialTz;
  }

  private get workflowNameDirty(): boolean {
    return this.workflowName.trim() !== this.workflowNameInitial.trim();
  }

  private get workflowDescriptionDirty(): boolean {
    return (
      this.workflowDescription.trim() !== this.workflowDescriptionInitial.trim()
    );
  }

  private normalizeWorkflowName(workflow: {
    id?: number;
    name?: string | null;
  }): string {
    const rawName =
      typeof workflow.name === 'string' ? workflow.name.trim() : '';
    if (rawName.length > 0) {
      return rawName;
    }
    if (typeof workflow.id === 'number') {
      return `Automation ${workflow.id}`;
    }
    return '';
  }

  private formatScheduleDate(
    value: string | null
  ): FormattedScheduleInfo | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const scheduleTimezone = this.cronTimezone || DEFAULT_CRON_TIMEZONE;
    const scheduledLabel = formatDateTime(date, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: scheduleTimezone,
    });
    const localLabel = formatDateTime(date, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const showLocal =
      Boolean(this.viewerTimezone) &&
      this.viewerTimezone !== scheduleTimezone &&
      localLabel !== scheduledLabel;
    return { scheduledLabel, localLabel, showLocal };
  }

  private renderScheduleHint(
    prefix: string,
    info: FormattedScheduleInfo | null,
    timezoneLabel: string
  ) {
    if (!info) {
      return nothing;
    }
    return html`
      <div class="hint-group">
        <div class="hint">
          ${prefix} ${info.scheduledLabel} (${timezoneLabel})
        </div>
        ${
          info.showLocal && this.viewerTimezone
            ? html`<div class="hint secondary">
                ${msg('Your local time:')} ${info.localLabel}
                (${this.viewerTimezone})
              </div>`
            : nothing
        }
      </div>
    `;
  }

  private onNameInput(event: Event) {
    const value = (event.target as unknown as { value: string }).value;
    this.workflowName = value;
    if (this.workflowNameError) {
      this.workflowNameError = '';
    }
  }

  private onCronInput(event: Event) {
    const value = (event.target as unknown as { value: string }).value;
    this.cronString = value;
  }

  private onDescriptionInput(event: Event) {
    const value = (event.target as unknown as { value: string }).value;
    this.workflowDescription = value;
    if (this.workflowDescriptionError) {
      this.workflowDescriptionError = '';
    }
  }

  private onTimezoneInput(event: Event) {
    const value = (event.target as unknown as { value: string }).value;
    this.cronTimezone = value;
  }

  private async saveWorkflowName() {
    if (
      this.workflowNameSaving ||
      typeof this.workflowId !== 'number' ||
      !this.workflowNameDirty
    ) {
      return;
    }

    const id = this.workflowId;
    const trimmed = this.workflowName.trim();
    const nextName = trimmed.length > 0 ? trimmed : null;

    const normalized = this.normalizeWorkflowName({ name: nextName, id });
    this.workflowName = normalized;
    this.workflowNameInitial = normalized;
    this.workflowNameSaving = true;
    this.workflowNameError = '';

    try {
      await api.workflows.update(id, { name: nextName });
      // Update the workflows list optimistically
      this.workflowsList?.updateWorkflowName?.(id, nextName);
    } catch (error) {
      this.workflowName = this.workflowNameInitial;
      this.workflowNameError =
        error instanceof Error
          ? error.message
          : msg('Failed to update workflow name');
    } finally {
      this.workflowNameSaving = false;
    }
  }

  private async saveWorkflowDescription() {
    if (
      this.workflowDescriptionSaving ||
      typeof this.workflowId !== 'number' ||
      !this.workflowDescriptionDirty
    ) {
      return;
    }

    const id = this.workflowId;
    const trimmed = this.workflowDescription.trim();
    const nextDescription = trimmed.length > 0 ? trimmed : null;

    this.workflowDescriptionSaving = true;
    this.workflowDescriptionError = '';

    try {
      await api.workflows.update(id, { description: nextDescription });
      this.workflowDescriptionInitial = nextDescription ?? '';
      this.workflowDescription = nextDescription ?? '';
      this.workflowsList?.load?.();
    } catch (error) {
      this.workflowDescription = this.workflowDescriptionInitial;
      this.workflowDescriptionError =
        error instanceof Error
          ? error.message
          : msg('Failed to update workflow description');
    } finally {
      this.workflowDescriptionSaving = false;
    }
  }

  private async onTemplateToggle(event: Event) {
    if (typeof this.workflowId !== 'number') {
      return;
    }

    const isTemplate = Boolean(
      (event.target as unknown as { checked?: boolean }).checked
    );

    if (this.workflowTemplateSaving || isTemplate === this.workflowIsTemplate) {
      return;
    }

    const id = this.workflowId;
    this.workflowIsTemplate = isTemplate;
    this.workflowTemplateSaving = true;
    this.workflowTemplateError = '';

    try {
      await api.workflows.update(id, { isTemplate });
      this.workflowTemplateInitial = isTemplate;
      this.workflowsList?.load?.();
    } catch (error) {
      this.workflowIsTemplate = this.workflowTemplateInitial;
      this.workflowTemplateError =
        error instanceof Error
          ? error.message
          : msg('Failed to update template status');
    } finally {
      this.workflowTemplateSaving = false;
    }
  }

  private async persistCron(payload: {
    cronString: string | null;
    cronTimezone: string | null;
  }) {
    if (typeof this.workflowId !== 'number') {
      return;
    }
    const id = this.workflowId;
    this.cronSaving = true;
    this.cronError = '';
    try {
      const updated = await api.workflows.update(id, payload);
      if (this.workflowId !== id) {
        return;
      }
      const cron = updated.cronString ?? '';
      const timezone = updated.cronTimezone ?? DEFAULT_CRON_TIMEZONE;
      this.cronString = cron;
      this.cronTimezone = cron ? timezone : DEFAULT_CRON_TIMEZONE;
      this.cronInitialString = cron;
      this.cronInitialTimezone = cron ? timezone : DEFAULT_CRON_TIMEZONE;
      this.cronNextExecutionAt = updated.cronNextExecutionAt ?? null;
      this.cronLastExecutedAt = updated.cronLastExecutedAt ?? null;
    } catch (error) {
      if (this.workflowId === id) {
        this.cronError =
          error instanceof Error
            ? error.message
            : msg('Failed to update schedule');
      }
    } finally {
      this.cronSaving = false;
    }
  }

  private async saveCronSettings() {
    if (
      this.cronSaving ||
      this.cronLoading ||
      typeof this.workflowId !== 'number' ||
      !this.cronDirty
    ) {
      return;
    }
    const cron = this.cronString.trim();
    if (!cron) {
      await this.persistCron({ cronString: null, cronTimezone: null });
      return;
    }
    const timezone =
      (this.cronTimezone || DEFAULT_CRON_TIMEZONE).trim() ||
      DEFAULT_CRON_TIMEZONE;
    await this.persistCron({ cronString: cron, cronTimezone: timezone });
  }

  private async clearCronSettings() {
    if (
      this.cronSaving ||
      this.cronLoading ||
      typeof this.workflowId !== 'number'
    ) {
      return;
    }
    await this.persistCron({ cronString: null, cronTimezone: null });
  }

  private get configValues(): PropertyItem[] {
    return getConfigRuntimeValues(this.runtimeValues);
  }

  private get secretValues(): PropertyItem[] {
    return getSecretRuntimeValues(this.runtimeValues);
  }

  private normalizeRuntimeValue(
    item: PropertyItem,
    kind: 'config' | 'secret'
  ): RuntimeValue {
    const normalized: RuntimeValue = {
      id: item.id,
      key: item.key,
      kind,
    };
    if (kind === 'config' || item.value !== undefined) {
      normalized.value = item.value ?? '';
    }
    if (kind === 'secret') {
      normalized.hasValue = item.hasValue ?? Boolean(item.value);
    }
    return normalized;
  }

  private mergeSavedRuntimeValue(
    saved: PropertyItem,
    submitted?: RuntimeValue
  ) {
    const normalized = this.toRuntimeValueItem(saved);
    let found = false;
    const mergeValue = (current?: RuntimeValue): RuntimeValue => {
      const value =
        normalized.kind === 'secret' &&
        this.revealedRuntimeValueIds.has(normalized.id)
          ? (submitted?.value ?? current?.value)
          : normalized.value;
      return {
        ...current,
        ...normalized,
        value,
      };
    };

    const updated = this.runtimeValues.map((value) => {
      if (value.id !== normalized.id) {
        return value;
      }
      found = true;
      return mergeValue(value);
    });

    this.runtimeValues = found ? updated : [...updated, mergeValue()];
  }

  private setRuntimeValueError(
    kind: 'config' | 'secret',
    message: string | null
  ) {
    this.runtimeValueErrors = {
      ...this.runtimeValueErrors,
      [kind]: message,
    };
  }

  private async onRuntimeValueAdded(
    kind: 'config' | 'secret',
    e: CustomEvent<PropertyItem>
  ) {
    const item = this.normalizeRuntimeValue(e.detail, kind);
    this.pendingRuntimeValues.add(item.id);
    try {
      const saved = await api.runtimeValues.save(item);
      this.mergeSavedRuntimeValue(saved, item);
      this.setRuntimeValueError(kind, null);
    } catch (err) {
      console.error('Failed to save runtime value', err);
      this.setRuntimeValueError(kind, msg('Failed to save runtime value'));
    } finally {
      this.pendingRuntimeValues.delete(item.id);
    }
  }

  private async onRuntimeValueChanged(
    kind: 'config' | 'secret',
    e: CustomEvent<PropertyItem>
  ) {
    const item = this.normalizeRuntimeValue(e.detail, kind);
    this.pendingRuntimeValues.add(item.id);
    try {
      const saved = await api.runtimeValues.save(item);
      this.mergeSavedRuntimeValue(saved, item);
      this.setRuntimeValueError(kind, null);
    } catch (err) {
      console.error('Failed to update runtime value', err);
      this.setRuntimeValueError(kind, msg('Failed to update runtime value'));
    } finally {
      this.pendingRuntimeValues.delete(item.id);
    }
  }

  private onRuntimeValueRemoveRequested(
    kind: 'config' | 'secret',
    e: CustomEvent<PropertyItem>
  ) {
    this.pendingRuntimeValueDelete = { kind, item: e.detail };
  }

  private cancelRuntimeValueDelete = () => {
    this.pendingRuntimeValueDelete = null;
  };

  private confirmRuntimeValueDelete = async () => {
    const pending = this.pendingRuntimeValueDelete;
    if (!pending) return;
    this.pendingRuntimeValueDelete = null;
    const { kind, item } = pending;
    const { id } = item;
    this.pendingRuntimeValues.add(id);
    try {
      await api.runtimeValues.remove(id);
      this.runtimeValues = this.runtimeValues.filter(
        (value) => value.id !== id
      );
      this.setRuntimeValueError(kind, null);
    } catch (err) {
      console.error('Failed to remove runtime value', err);
      this.setRuntimeValueError(kind, msg('Failed to remove runtime value'));
    } finally {
      this.pendingRuntimeValues.delete(id);
    }
  };

  private async onSecretRevealToggled(e: CustomEvent<PropertyItem>) {
    const { id } = e.detail;
    if (this.revealedRuntimeValueIds.has(id)) {
      const nextRevealed = new Set(this.revealedRuntimeValueIds);
      nextRevealed.delete(id);
      this.revealedRuntimeValueIds = nextRevealed;
      this.runtimeValues = hideRuntimeValue(this.runtimeValues, id);
      return;
    }

    try {
      const revealed = await api.runtimeValues.reveal(id);
      this.runtimeValues = mergeRevealedRuntimeValue(
        this.runtimeValues,
        revealed
      );
      this.revealedRuntimeValueIds = new Set([
        ...this.revealedRuntimeValueIds,
        id,
      ]);
    } catch (err) {
      console.error('Failed to reveal runtime value', err);
    }
  }

  private toRuntimeValueItem(detail: PropertyItem): RuntimeValue {
    return {
      id: detail.id,
      key: detail.key,
      kind: detail.kind ?? 'config',
      value: detail.kind === 'secret' ? undefined : (detail.value ?? ''),
      hasValue: detail.kind === 'secret' ? detail.hasValue : undefined,
    };
  }

  private onRuntimeValueCreated = (e: Event) => {
    const detail = (e as CustomEvent<{ id: string }>).detail;
    if (!detail?.id) return;
    if (this.pendingRuntimeValues.has(detail.id)) {
      return;
    }
    void this.loadRuntimeValues();
  };

  private onRuntimeValueChangedWs = (e: Event) => {
    const detail = (e as CustomEvent<{ id: string }>).detail;
    if (!detail?.id) return;
    if (this.pendingRuntimeValues.has(detail.id)) {
      return;
    }
    void this.loadRuntimeValues();
  };

  private onRuntimeValueRemovedWs = (e: Event) => {
    const detail = (e as CustomEvent<{ id: string }>).detail;
    if (!detail?.id) return;
    if (this.pendingRuntimeValues.has(detail.id)) {
      return;
    }
    void this.loadRuntimeValues();
  };

  private onWorkflowUpdated = (e: Event) => {
    const detail = (
      e as CustomEvent<{
        workflowId: number;
        cronLastExecutedAt?: string | null;
        cronNextExecutionAt?: string | null;
      }>
    ).detail;

    // Only update if this is for the current workflow
    if (this.workflowId === detail.workflowId) {
      if (detail.cronLastExecutedAt !== undefined) {
        this.cronLastExecutedAt = detail.cronLastExecutedAt;
      }
      if (detail.cronNextExecutionAt !== undefined) {
        this.cronNextExecutionAt = detail.cronNextExecutionAt;
      }
    }
  };

  render() {
    const hasWorkflow = typeof this.workflowId === 'number';
    const isBusy = this.cronSaving || this.cronLoading;
    const cronValue = this.cronString ?? '';
    const timezoneValue = this.cronTimezone ?? DEFAULT_CRON_TIMEZONE;
    const normalizedCron = cronValue.trim();
    const initialCron = this.cronInitialString.trim();
    const canClear = Boolean(normalizedCron || initialCron);
    const nextExecutionInfo = this.formatScheduleDate(this.cronNextExecutionAt);
    const lastExecutionInfo = this.formatScheduleDate(this.cronLastExecutedAt);
    const timezoneLabel = timezoneValue || DEFAULT_CRON_TIMEZONE;
    const workflowNameValue = this.workflowName ?? '';
    const isNameBusy = this.workflowNameSaving;
    const workflowNameError = this.workflowNameError;
    const workflowDescriptionValue = this.workflowDescription ?? '';
    const isDescriptionBusy = this.workflowDescriptionSaving;
    const workflowDescriptionError = this.workflowDescriptionError;
    const workflowIsTemplate = this.workflowIsTemplate;
    const isTemplateBusy = this.workflowTemplateSaving;
    const workflowTemplateError = this.workflowTemplateError;

    return html`
      <section>
        <h3>${msg('Workflow')}</h3>
        <section data-testid="inspector-config">
          <div class="config-field">
            <div class="config-field-label-wrap">
              <span class="config-field-label">${msg('Name')}</span>
            </div>
            <div class="config-field-control">
              <sp-textfield
                .value=${workflowNameValue}
                ?disabled=${!hasWorkflow || isNameBusy}
                @input=${this.onNameInput}
                @change=${this.saveWorkflowName}
                @focusout=${this.saveWorkflowName}
              ></sp-textfield>
            </div>
          </div>
          <div class="config-field config-field--stacked">
            <div class="config-field-label-wrap">
              <span class="config-field-label">${msg('Description')}</span>
            </div>
            <div class="config-field-control">
              <sb-code-editor
                .value=${workflowDescriptionValue}
                mode="template"
                placeholder=${msg('Describe what this workflow does.')}
                .rows=${4}
                .maxRows=${12}
                ?disabled=${!hasWorkflow || isDescriptionBusy}
                @input=${this.onDescriptionInput}
                @editor-blur=${this.saveWorkflowDescription}
              ></sb-code-editor>
            </div>
          </div>
          <div class="config-field">
            <div class="config-field-label-wrap">
              <span class="config-field-label">${msg('Use as template')}</span>
            </div>
            <div class="config-field-control">
              <sp-switch
                .checked=${live(workflowIsTemplate)}
                ?disabled=${!hasWorkflow || isTemplateBusy}
                @change=${this.onTemplateToggle}
              ></sp-switch>
            </div>
          </div>
        </section>
        ${
          workflowNameError
            ? html`<div class="error">${workflowNameError}</div>`
            : nothing
        }
        ${
          workflowDescriptionError
            ? html`<div class="error">${workflowDescriptionError}</div>`
            : nothing
        }
        <span class="hint">
          ${msg('Templates appear in the workflow library for new workflows.')}
        </span>
        ${
          workflowTemplateError
            ? html`<div class="error">${workflowTemplateError}</div>`
            : nothing
        }
      </section>
      <section>
        <h3>${msg('Triggers')}</h3>
        <section data-testid="inspector-config">
          <div class="config-field">
            <div class="config-field-label-wrap">
              <span class="config-field-label">${msg('Cron')}</span>
            </div>
            <div class="config-field-control">
              <sp-textfield
                placeholder="* * * * *"
                .value=${cronValue}
                ?disabled=${!hasWorkflow || this.cronLoading}
                @input=${this.onCronInput}
              ></sp-textfield>
            </div>
          </div>
          <div class="config-field">
            <div class="config-field-label-wrap">
              <span class="config-field-label">${msg('Timezone')}</span>
            </div>
            <div class="config-field-control">
              <sp-textfield
                .value=${timezoneValue}
                ?disabled=${!hasWorkflow || isBusy}
                @input=${this.onTimezoneInput}
              ></sp-textfield>
            </div>
          </div>
        </section>
        <span class="hint">${msg('Uses minute-hour-day-month-weekday format.')}</span>
        <div class="actions">
          <sp-action-button
            label=${msg('Save schedule')}
            ?disabled=${!hasWorkflow || isBusy || !this.cronDirty}
            @click=${this.saveCronSettings}
          >
            ${msg('Save schedule')}
          </sp-action-button>
          <sp-action-button
            label=${msg('Clear schedule')}
            quiet
            ?disabled=${!hasWorkflow || isBusy || !canClear}
            @click=${this.clearCronSettings}
          >
            ${msg('Clear schedule')}
          </sp-action-button>
        </div>
        ${this.renderScheduleHint(
          msg('Last run completed at'),
          lastExecutionInfo,
          timezoneLabel
        )}
        ${this.renderScheduleHint(
          msg('Next run scheduled at'),
          nextExecutionInfo,
          timezoneLabel
        )}
        ${
          this.cronError
            ? html`<div class="error">${this.cronError}</div>`
            : nothing
        }
      </section>
      <section>
        <h3>${msg('Webhooks')}</h3>
        <p class="webhook-description">
          ${msg('Generate webhook URLs to trigger this workflow from external systems.')}
        </p>
        <div class="webhook-actions">
          <sp-action-button
            label=${msg('Create webhook')}
            ?disabled=${!hasWorkflow || this.webhookLoading}
            @click=${() => this.createWebhook()}
          >
            ${msg('Create webhook')}
          </sp-action-button>
        </div>
        ${this.renderWebhookList()}
      </section>
      <section>
        <h3>${msg('Config values')}</h3>
        <sb-property-table
          add-label=${msg('Add config entry')}
          .data=${this.configValues}
          .reservedKeys=${this.secretValues.map((value) => value.key)}
          @data-added=${(e: CustomEvent<PropertyItem>) =>
            this.onRuntimeValueAdded('config', e)}
          @data-changed=${(e: CustomEvent<PropertyItem>) =>
            this.onRuntimeValueChanged('config', e)}
          @data-removed=${(e: CustomEvent<PropertyItem>) =>
            this.onRuntimeValueRemoveRequested('config', e)}
        ></sb-property-table>
        ${
          this.runtimeValueErrors.config
            ? html`<div class="error">${this.runtimeValueErrors.config}</div>`
            : nothing
        }
      </section>
      <section>
        <h3>${msg('Secrets')}</h3>
        <sb-property-table
          add-label=${msg('Add secret value')}
          secret-values
          .data=${this.secretValues}
          .reservedKeys=${this.configValues.map((value) => value.key)}
          .revealedIds=${this.revealedRuntimeValueIds}
          @data-added=${(e: CustomEvent<PropertyItem>) =>
            this.onRuntimeValueAdded('secret', e)}
          @data-changed=${(e: CustomEvent<PropertyItem>) =>
            this.onRuntimeValueChanged('secret', e)}
          @data-removed=${(e: CustomEvent<PropertyItem>) =>
            this.onRuntimeValueRemoveRequested('secret', e)}
          @secret-reveal-toggled=${(e: CustomEvent<PropertyItem>) =>
            this.onSecretRevealToggled(e)}
        ></sb-property-table>
        ${
          this.runtimeValueErrors.secret
            ? html`<div class="error">${this.runtimeValueErrors.secret}</div>`
            : nothing
        }
      </section>
      ${
        this.pendingWebhookDelete
          ? html`
            <kv-confirmation-dialog
              heading=${msg('Delete webhook')}
              confirm-label=${msg('Delete')}
              .confirming=${this.webhookLoading}
              @cancel=${this.cancelDeleteWebhook}
              @confirm=${this.confirmDeleteWebhook}
            >
              <p>
                ${msg(
                  str`Delete webhook "${this.pendingWebhookDelete.id}"? External systems using ${workflowService.getWebhookUrl(this.pendingWebhookDelete)} will no longer be able to trigger this automation.`
                )}
              </p>
            </kv-confirmation-dialog>
          `
          : nothing
      }
      ${
        this.pendingRuntimeValueDelete
          ? html`
            <kv-confirmation-dialog
              heading=${
                this.pendingRuntimeValueDelete.kind === 'secret'
                  ? msg('Delete secret value')
                  : msg('Delete config value')
              }
              confirm-label=${msg('Delete')}
              @cancel=${this.cancelRuntimeValueDelete}
              @confirm=${this.confirmRuntimeValueDelete}
            >
              <p>
                ${
                  this.pendingRuntimeValueDelete.kind === 'secret'
                    ? msg(
                        str`Delete secret "${this.pendingRuntimeValueDelete.item.key}"? Workflows referencing it will stop resolving this secret value.`
                      )
                    : msg(
                        str`Delete config value "${this.pendingRuntimeValueDelete.item.key}"? Workflows referencing it will stop resolving this value.`
                      )
                }
              </p>
            </kv-confirmation-dialog>
          `
          : nothing
      }
    `;
  }
}

customElements.define('sb-settings', SbSettings);
